import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, CheckCircle, AlertCircle, MessageSquare, 
  ChevronDown, ChevronUp, Calendar, DollarSign, 
  Briefcase, User, Mail, Phone, Building
} from 'lucide-react';

/**
 * QuoteCard - Reusable component for displaying quote requests
 * Used in both Customer Portal and Admin Panel
 * 
 * @param {Object} quote - The quote data object
 * @param {string} language - Current language ('tr' or 'en')
 * @param {boolean} isAdmin - If true, shows admin-only fields (admin_notes, user info)
 * @param {function} onStatusChange - Callback for status changes (admin only)
 */
const QuoteCard = ({ quote, language = 'tr', isAdmin = false, onStatusChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Status configuration
  const statusConfig = {
    new: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      icon: Clock,
      labelTR: 'Yeni',
      labelEN: 'New'
    },
    in_review: {
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      icon: AlertCircle,
      labelTR: 'İnceleniyor',
      labelEN: 'In Review'
    },
    responded: {
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      icon: MessageSquare,
      labelTR: 'Yanıtlandı',
      labelEN: 'Responded'
    },
    closed: {
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
      icon: CheckCircle,
      labelTR: 'Kapatıldı',
      labelEN: 'Closed'
    }
  };

  // Service type labels
  const serviceLabels = {
    'ecommerce': { tr: 'E-Ticaret & Marketplace', en: 'E-Commerce & Marketplace' },
    'web-app': { tr: 'Web & Uygulama Geliştirme', en: 'Web & App Development' },
    'automation': { tr: 'Otomasyon & Entegrasyon', en: 'Automation & Integration' },
    'ai': { tr: 'AI Ajanları & Chatbotlar', en: 'AI Agents & Chatbots' },
    '3d': { tr: '3D Modelleme & Baskı', en: '3D Modeling & Printing' },
    'marketing': { tr: 'Performans Pazarlama', en: 'Performance Marketing' }
  };

  // Budget range labels
  const budgetLabels = {
    '5k-10k': '$5,000 - $10,000',
    '10k-25k': '$10,000 - $25,000',
    '25k-50k': '$25,000 - $50,000',
    '50k+': '$50,000+',
    'flexible': language === 'tr' ? 'Esnek / Teklife Göre' : 'Flexible / Quote-Based'
  };

  const status = statusConfig[quote.status] || statusConfig.new;
  const StatusIcon = status.icon;
  const serviceName = serviceLabels[quote.service_type]?.[language === 'tr' ? 'tr' : 'en'] || 
                      quote.service_type || 
                      (language === 'tr' ? 'Genel Talep' : 'General Request');

  // Truncate description for preview
  const maxPreviewLength = 150;
  const hasLongDescription = quote.description && quote.description.length > maxPreviewLength;
  const previewDescription = hasLongDescription 
    ? quote.description.substring(0, maxPreviewLength) + '...'
    : quote.description;

  return (
    <motion.div
      layout
      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
    >
      {/* Card Header - Always Visible */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {serviceName}
              </h3>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {language === 'tr' ? status.labelTR : status.labelEN}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {language === 'tr' ? 'Oluşturulma: ' : 'Created: '}
              {new Date(quote.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Description Preview */}
        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
          {isExpanded ? quote.description : previewDescription}
        </p>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          data-testid="quote-expand-toggle"
        >
          {isExpanded ? (
            <>
              {language === 'tr' ? 'Daha Az Göster' : 'Show Less'}
              <ChevronUp className="w-4 h-4 ml-1" />
            </>
          ) : (
            <>
              {language === 'tr' ? 'Daha Fazlasını Göster' : 'Show More'}
              <ChevronDown className="w-4 h-4 ml-1" />
            </>
          )}
        </button>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 pt-2 border-t border-gray-100 dark:border-gray-800">
              {/* Details Grid */}
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                {/* Service Type */}
                <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <Briefcase className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      {language === 'tr' ? 'Hizmet Türü' : 'Service Type'}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {serviceName}
                    </p>
                  </div>
                </div>

                {/* Budget Range */}
                {quote.budget_range && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                        {language === 'tr' ? 'Bütçe Aralığı' : 'Budget Range'}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {budgetLabels[quote.budget_range] || quote.budget_range}
                      </p>
                    </div>
                  </div>
                )}

                {/* Target Date */}
                {quote.target_date && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                        {language === 'tr' ? 'Hedef Tarih' : 'Target Date'}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {new Date(quote.target_date).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {/* Status */}
                <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <StatusIcon className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      {language === 'tr' ? 'Durum' : 'Status'}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {language === 'tr' ? status.labelTR : status.labelEN}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Info (Admin Only) */}
              {isAdmin && (
                <div className="grid sm:grid-cols-2 gap-4 mb-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{quote.full_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${quote.email}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                      {quote.email}
                    </a>
                  </div>
                  {quote.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a href={`tel:${quote.phone}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                        {quote.phone}
                      </a>
                    </div>
                  )}
                  {quote.company_name && (
                    <div className="flex items-center gap-3">
                      <Building className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{quote.company_name}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Customer Response */}
              {quote.customer_notes && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mt-4">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                    {language === 'tr' ? 'Yanıt:' : 'Response:'}
                  </p>
                  <p className="text-sm text-blue-800 dark:text-blue-200">{quote.customer_notes}</p>
                  {quote.responded_at && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                      {new Date(quote.responded_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              )}

              {/* Admin Notes (Admin Only) */}
              {isAdmin && quote.admin_notes && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mt-4">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-300 mb-2">
                    {language === 'tr' ? 'İç Notlar:' : 'Internal Notes:'}
                  </p>
                  <p className="text-sm text-amber-800 dark:text-amber-200">{quote.admin_notes}</p>
                </div>
              )}

              {/* Admin Actions */}
              {isAdmin && onStatusChange && (
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {language === 'tr' ? 'Durumu Güncelle:' : 'Update Status:'}
                  </span>
                  <select
                    value={quote.status}
                    onChange={(e) => onStatusChange(quote.id, e.target.value)}
                    className="px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="quote-status-select"
                  >
                    <option value="new">{language === 'tr' ? 'Yeni' : 'New'}</option>
                    <option value="in_review">{language === 'tr' ? 'İnceleniyor' : 'In Review'}</option>
                    <option value="responded">{language === 'tr' ? 'Yanıtlandı' : 'Responded'}</option>
                    <option value="closed">{language === 'tr' ? 'Kapatıldı' : 'Closed'}</option>
                  </select>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default QuoteCard;
